import { AGE_STATUS } from "#app/lib/age-status";

/**
 * The "no value" group sentinel. Mirrors the component library's
 * `UNASSIGNED_GROUP_KEY` AND the core `issueGroupCounts` resolver, which both
 * emit this exact string for null group keys. Re-declared here (not imported)
 * because this module is pure-logic and runs in the node vitest harness, which
 * cannot load the component library. The value is a fixed
 * cross-system contract — keep these three in sync.
 */
export const UNASSIGNED_GROUP_KEY = "__unassigned__";

// Shared issue-grouping selectors — the single source of truth for how a
// grouping dimension + group key map to (a) a GraphQL `where` selector and (b)
// the server `groupBy` field that feeds `useIssueGroupCounts`. Consumed by both
// the issues table and the workflow board so the two screens can never drift.
// Pure (no React / Apollo) so it unit-tests in the node vitest runner.

/**
 * The dimensions an issue screen can group by. `assignee` is USERS-ONLY — a
 * team is its own dimension (`team`), resolved server-side via the
 * `assigneeTeamIds` membership filter, never by stuffing a team id into the
 * assignee entityId list (a team id is not a user entityId, so that never
 * matched). A single-workflow board groups by `stage`; a cross-workflow board
 * (a team or member) groups by `workflow`. The table adds `issueType`.
 */
export type IssueGroupDimension =
  | "stage"
  | "workflow"
  | "assignee"
  | "team"
  | "age"
  | "issueType";

/**
 * The server `groupBy` field for each dimension — the allowlist the core
 * `issueGroupCounts` resolver accepts (`stageId | issueTypeId | workflowId |
 * age.status | assignees.entityId | team`). Feeds `useIssueGroupCounts`.
 */
export const ISSUE_GROUP_SERVER_KEY: Record<IssueGroupDimension, string> = {
  stage: "stageId",
  workflow: "workflowId",
  assignee: "assignees.entityId",
  team: "team",
  age: "age.status",
  issueType: "issueTypeId",
};

/**
 * Build the GraphQL `where` selector for one group of a dimension. `key` is the
 * group's id (a stageId / entityId / teamId / age status / issueTypeId) or
 * {@link UNASSIGNED_GROUP_KEY} for the "no value" bucket — the same sentinel the
 * server emits for null keys in `issueGroupCounts`.
 */
export function buildGroupWhere(
  dimension: IssueGroupDimension,
  key: string,
): Record<string, unknown> {
  const unassigned = key === UNASSIGNED_GROUP_KEY;
  switch (dimension) {
    case "stage":
      return unassigned
        ? { stageId: { exists: false } }
        : { stageId: { eq: key } };
    case "workflow":
      return unassigned
        ? { workflowId: { exists: false } }
        : { workflowId: { eq: key } };
    case "assignee":
      return unassigned
        ? { assignees: { entityId: { isEmpty: true } } }
        : { assignees: { entityId: { hasSome: [key] } } };
    case "team":
      return unassigned
        ? { assigneeTeamIds: { isEmpty: true } }
        : { assigneeTeamIds: { hasSome: [key] } };
    case "age":
      return unassigned
        ? { age: { status: { in: [null] } } }
        : { age: { status: { eq: key } } };
    case "issueType":
      return unassigned
        ? { issueTypeId: { exists: false } }
        : { issueTypeId: { eq: key } };
  }
}

/**
 * The age grouping buckets, in display order. `disabled` and the unassigned
 * bucket keep their display labels ("Disabled" / "No age tracking") rather than
 * the raw `AGE_STATUS_CONFIG` label ("Disable age").
 */
export const AGE_GROUPS: ReadonlyArray<{ key: string; label: string }> = [
  { key: AGE_STATUS.onTrack, label: "On Track" },
  { key: AGE_STATUS.atRisk, label: "At Risk" },
  { key: AGE_STATUS.offTrack, label: "Off Track" },
  { key: AGE_STATUS.disabled, label: "Disabled" },
  { key: UNASSIGNED_GROUP_KEY, label: "No age tracking" },
];

/** The muted "no value" label per dimension, for group/column headers. */
export const UNASSIGNED_DIMENSION_LABEL: Record<IssueGroupDimension, string> = {
  stage: "No stage",
  workflow: "No workflow",
  assignee: "No assignee",
  team: "No team",
  age: "No age tracking",
  issueType: "No type",
};
